import { Trans, useLingui } from '@lingui/react';
import { CircularProgress } from '@startuptools/ui/CircularProgress';
import { Divider } from '@startuptools/ui/Divider';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Box } from '@startuptools/ui/Box';
import { Chip } from '@startuptools/ui/Chip';
import { Modal } from '@startuptools/ui/Modal';
import { ConfirmDialog } from '@startuptools/ui/ConfirmDialog';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { toast } from '@startuptools/ui/toast/use-toast';
import { DocumentDialog } from './DocumentDialog';
import { SigningStatus } from './ScriveStatus';
import { Action, Actions } from './Actions';
import {
  GqlDocGenState,
  GqlDocSystemTagKind,
  type GqlBolagoFile,
  GqlDocumentSignatureMethod,
  GqlScriveDocumentStatus,
  GqlSeverity,
  GqlSignAllowedAction,
  type GqlValidatorStatus,
} from '../../graphql/base-types.graphql';
import { PDFPreview } from './PDFPreview';
import { DocumentCreateDialog, type UploadDocForm } from './DocumentCreateDialog';
import { ValidatorError, ValidatorErrorDialog } from '../ValidatorErrorDialog';
import { isNil, orderBy } from 'lodash-es';
import { useFileGenStateQuery } from './Document.graphql';
import React from 'react';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { BolagoFile, FileGenState } from '@startuptools/db-schema';

export type DisplayDocument = Pick<
  GqlBolagoFile,
  | 'id'
  | 'genState'
  | 'createdAt'
  | 'filesize'
  | 'dirty'
  | 'scriveStatus'
  | 'zignedAgreementStatus'
  | 'signatureMethod'
  | 'filename'
  | 'uploaded'
  | 'signAllowedAction'
  | 'kind'
  | 'deletable'
  | 'hasThumbnail'
  | 'mimeType'
  | 'companyId'
>;

export type DrizzleDisplayDocument = Pick<
  BolagoFile,
  | 'id'
  | 'genState'
  | 'createdAt'
  | 'filesize'
  | 'dirty'
  | 'signatureMethod'
  | 'filename'
  | 'uploaded'
  | 'kind'
  | 'hasThumbnail'
  | 'mimeType'
>;

export const usePollForGenState = (document: Pick<DisplayDocument, 'id' | 'genState'> | undefined | null) => {
  const { stopPolling, startPolling } = useFileGenStateQuery({
    variables: { id: document?.id ?? '' },
    skip: isNil(document),
    pollInterval: 2000,
  });

  React.useEffect(() => {
    if (!document) {
      return;
    }
    if ([GqlDocGenState.Completed, GqlDocGenState.Failed].includes(document.genState)) {
      stopPolling();
    } else {
      startPolling(2000);
    }
  }, [document, startPolling, stopPolling]);
};

export const Document = ({
  defaultFileName,
  document,
  documentSystemTagKind,
  hasDigitalRegistrationForms,
  generate,
  allowDigitalSignature = true,
  preview,
  validate = () => Promise.resolve({ isValid: true, errors: undefined }),
  upload,
  onDelete,
  signaturesSlot,
  tagsSlot,
  showCustomTag = true,
  allowGenerationWithWarnings = true,
  navigateToBilling,
  hasActiveSubscription = true,
  canUseProFeatures = false,
}: {
  allowGenerationWithWarnings?: boolean;
  defaultFileName?: string;
  document: DisplayDocument | DrizzleDisplayDocument | undefined | null;
  documentSystemTagKind?: GqlDocSystemTagKind;
  hasDigitalRegistrationForms?: boolean;
  generate?: (signatureMethod: GqlDocumentSignatureMethod) => Promise<unknown>;
  allowDigitalSignature?: boolean;
  preview?: () => Promise<Blob>;
  upload?: (data: UploadDocForm) => Promise<unknown>;
  onDelete?: () => Promise<unknown>;
  /* if you return errors in this function they will be shown inside the Document component, negating the need
  to implement/call the error Dialog yourself. Errors with the level warning will allow the document to be previewed, but
  block them to be generated. Errors with the level error will block both preview and generation.  */
  validate?: () => Promise<{
    isValid: boolean;
    errors: (GqlValidatorStatus & { action?: React.ReactNode })[] | undefined;
  }>;
  signaturesSlot?: React.ReactNode;
  tagsSlot?: React.ReactNode;
  showCustomTag?: boolean;
  navigateToBilling?: () => void;
  hasActiveSubscription?: boolean;
  canUseProFeatures?: boolean;
}) => {
  const [validatorDialog, setValidatorDialog] = React.useState<GqlValidatorStatus[] | undefined>(undefined);
  const [showPdfPreview, setShowPdfPreview] = React.useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = React.useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = React.useState(false);
  const showDocument = () => setShowDocumentDialog(true);

  const [showCreateDocumentDialog, setShowCreateDocumentDialog] = React.useState<false | 'upload' | 'generate'>(false);

  const [localDisabled, setLocalDisabled] = React.useState(false);

  const isGenerating = Boolean(
    localDisabled ||
      (document &&
        typeof document.genState !== 'number' &&
        document.genState !== GqlDocGenState.Completed &&
        document.genState !== GqlDocGenState.Failed),
  );
  const { i18n } = useLingui();
  usePollForGenState(typeof document?.genState === 'number' ? undefined : document);

  const generateWrapped = generate
    ? async (signatureMethod: GqlDocumentSignatureMethod) => {
        setLocalDisabled(true);
        setShowCreateDocumentDialog(false);
        const { isValid } = await validate();
        if (isValid) {
          try {
            await generate(signatureMethod);
          } catch {
            toast({
              title: <Trans id="Failed to generate document" />,
              description: <Trans id="Please contact support if the problem persists" />,
              severity: 'danger',
            });
          }
        }
        setLocalDisabled(false);
      }
    : undefined;

  const isCustomUpload =
    document?.uploaded &&
    ![GqlDocSystemTagKind.MeetingSummonsCoverSheet, GqlDocSystemTagKind.MeetingAppendix].includes(
      document.kind as GqlDocSystemTagKind, // GqlDocSystemTagKind & FileSystemTagKind are identical
    );

  const showSignatureStatus =
    document?.signatureMethod === GqlDocumentSignatureMethod.Digital &&
    'scriveStatus' in document &&
    (document.scriveStatus || document.zignedAgreementStatus);

  const isRegForm =
    documentSystemTagKind &&
    [
      GqlDocSystemTagKind.WarrantRegistrationForm,
      GqlDocSystemTagKind.ShareIssueRegistrationForm,
      GqlDocSystemTagKind.ConvertibleRegistrationForm,
      GqlDocSystemTagKind.AuthorizationRegistrationForm,
      GqlDocSystemTagKind.ArticlesOfAssocRegForm,
    ].includes(documentSystemTagKind);

  const showQualifiedEsignWarning =
    hasDigitalRegistrationForms === true && document?.signatureMethod === GqlDocumentSignatureMethod.Analogue;

  return (
    <Stack direction="row" gap={2} justifyContent="space-between" width="100%">
      <Stack justifyContent="center">
        <Stack direction="row" spacing={1}>
          {document?.dirty || document?.genState === GqlDocGenState.Failed || showQualifiedEsignWarning ? (
            <Tooltip
              title={
                document?.dirty ? (
                  <Trans id="Contains outdated information and should be re-generated" />
                ) : showQualifiedEsignWarning ? (
                  <Trans id="There are other digital registration forms that uses Qualified E-signing and therefore you should use the same method here" />
                ) : (
                  <Trans id="Failed to generate document, contact support" />
                )
              }
            >
              <ReportProblemIcon color="warning" />
            </Tooltip>
          ) : (
            <InsertDriveFileIcon color="primary" />
          )}

          <Typography>{document?.filename ?? defaultFileName}</Typography>
        </Stack>
        <Stack direction="row">
          {isGenerating ? (
            <Typography fontSize={13}>
              <Trans id="Generating..." />
            </Typography>
          ) : (
            document && (
              <Stack alignItems="center" direction="row" gap={1}>
                {document?.genState !== GqlDocGenState.Failed && (
                  <Typography fontSize={13}>
                    <Trans id="Created" comment="when a document was created" />{' '}
                    <DateFormat locale={i18n.locale} relative readable>
                      {document.createdAt}
                    </DateFormat>
                  </Typography>
                )}
                {document.filesize && (
                  <>
                    <Divider orientation="vertical" />
                    <Typography fontSize={13}>
                      <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                        {document.filesize / 1000}
                      </NumberFormat>
                      kB
                    </Typography>
                  </>
                )}
                {isCustomUpload && showCustomTag && (
                  <>
                    <Divider orientation="vertical" />
                    <Chip variant="soft" color="primary" size="sm">
                      <Trans id="Custom document" />
                    </Chip>
                  </>
                )}
                {document.dirty && generate && (
                  <>
                    <Divider orientation="vertical" />
                    <Typography color="danger" fontSize={13}>
                      <Trans id="Contains outdated information and should be re-generated" />
                    </Typography>
                  </>
                )}
                {showQualifiedEsignWarning && generate && (
                  <>
                    <Divider orientation="vertical" />
                    <Typography color="danger" fontSize={13}>
                      <Trans id="There are other digital registration forms that uses Qualified E-signing and therefore you should use the same method here" />
                    </Typography>
                  </>
                )}
              </Stack>
            )
          )}
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" justifySelf="end" gap={1}>
        {isGenerating && <CircularProgress size="sm" />}
        {showSignatureStatus && (
          <Box onClick={showDocument} sx={{ cursor: 'pointer' }}>
            <SigningStatus status={document.zignedAgreementStatus || document.scriveStatus!} />
          </Box>
        )}

        <Actions disabled={isGenerating}>
          <Action
            show={Boolean(preview) && !document?.uploaded}
            onClick={async () => {
              const { isValid, errors } = await validate();
              if (
                isValid ||
                (errors &&
                  !errors.some(
                    e => e.hasError && (allowGenerationWithWarnings ? e.severity === GqlSeverity.Error : true),
                  ))
              ) {
                setShowPdfPreview(true);
              } else {
                setValidatorDialog(errors);
              }
            }}
            visible={!document || document.dirty || showQualifiedEsignWarning}
            variant="outlined"
          >
            <Trans id="Preview" />
          </Action>

          <Action
            show={Boolean(upload)}
            onClick={() => {
              if (hasActiveSubscription) {
                setShowCreateDocumentDialog('upload');
              } else {
                setShowUpgradeDialog(true);
              }
            }}
            visible={!document && !generateWrapped}
          >
            {document?.uploaded ? <Trans id="Replace document" /> : <Trans id="Upload document" />}
          </Action>

          <Action
            show={Boolean(generateWrapped)}
            visible={
              !document || document.dirty || document.genState !== GqlDocGenState.Completed || showQualifiedEsignWarning
            }
            onClick={async () => {
              if (validate) {
                const { isValid, errors } = await validate();
                if (!isValid) {
                  if (errors && errors.some(e => e.hasError)) {
                    setValidatorDialog(errors);
                  }
                  return;
                }
              }
              if (allowDigitalSignature) {
                setShowCreateDocumentDialog('generate');
                return;
              }
              return generateWrapped!(GqlDocumentSignatureMethod.Analogue);
            }}
          >
            <Trans id="Create Document" />
          </Action>

          <Action visible={false} onClick={showDocument} show={Boolean(document?.dirty)}>
            <Trans id="Signatures" />
          </Action>

          <Action
            onClick={showDocument}
            visible={!document?.dirty || !showQualifiedEsignWarning}
            show={document?.genState === GqlDocGenState.Completed || document?.genState === FileGenState.Completed}
          >
            {document?.signatureMethod === GqlDocumentSignatureMethod.Digital &&
            'signAllowedAction' in document &&
            [GqlSignAllowedAction.Start, GqlSignAllowedAction.Restart].includes(document.signAllowedAction) &&
            !document.dirty ? (
              <Trans id="Start signing process" />
            ) : (
              <Trans id="Show File" />
            )}
          </Action>

          {document && 'deletable' in document && onDelete && (
            <Action show={document.deletable ?? false} visible={false} onClick={onDelete}>
              <Trans id="Delete" />
            </Action>
          )}
        </Actions>
      </Stack>
      {showDocumentDialog && (
        <Modal open onClose={() => setShowDocumentDialog(false)}>
          <ModalDialog sx={{ maxWidth: '95%', maxHeight: '95%', minWidth: 1000, overflowY: 'auto', paddingX: 2 }}>
            <ModalClose />
            {document && (
              <DocumentDialog document={document} moreDetails={tagsSlot}>
                {signaturesSlot}
              </DocumentDialog>
            )}
          </ModalDialog>
        </Modal>
      )}
      {showPdfPreview && (
        <PDFPreview
          onClose={() => {
            setShowPdfPreview(false);
          }}
          mode="generate"
          generatePdf={async () => {
            if (!preview) throw new Error('preview is undefined');
            const blob = await preview();
            return { blob, filename: defaultFileName ?? 'document.pdf' };
          }}
        />
      )}
      {showUpgradeDialog && (
        <ConfirmDialog
          header={<Trans id="To upload documents an active subscription is required" />}
          confirmProps={{ children: <Trans id="Upgrade" /> }}
          onCancel={() => setShowUpgradeDialog(false)}
          onConfirm={() => {
            setShowUpgradeDialog(false);
            navigateToBilling?.();
          }}
        >
          <Trans id="Upgrade your subscription to gain access to document management including e-signing" />
        </ConfirmDialog>
      )}
      {showCreateDocumentDialog && (
        <DocumentCreateDialog
          allowDigitalSignature={allowDigitalSignature}
          mode={showCreateDocumentDialog}
          showExistingDocWarning={Boolean(document?.uploaded) && !showQualifiedEsignWarning}
          showSigningWarning={
            document?.signatureMethod === GqlDocumentSignatureMethod.Digital &&
            'scriveStatus' in document &&
            document.scriveStatus === GqlScriveDocumentStatus.Pending
          }
          upload={async data => {
            if (showCreateDocumentDialog === 'upload') {
              if (!upload) {
                throw new Error('You must provide the upload function');
              }
              await upload(data);
              setShowCreateDocumentDialog(false);
            } else {
              await generateWrapped!(
                data.signDigitally ? GqlDocumentSignatureMethod.Digital : GqlDocumentSignatureMethod.Analogue,
              );
              setShowCreateDocumentDialog(false);
            }
          }}
          onClose={() => {
            setShowCreateDocumentDialog(false);
          }}
          isRegForm={isRegForm}
          canUseProFeatures={canUseProFeatures}
          hasDigitalRegistrationForms={hasDigitalRegistrationForms}
        />
      )}
      {validatorDialog && (
        <ValidatorErrorDialog
          allowGenerationWithWarnings={allowGenerationWithWarnings}
          onClose={() => setValidatorDialog(undefined)}
          preview={() => {
            setValidatorDialog(undefined);
            return setShowPdfPreview(true);
          }}
          hasFatalErrors={validatorDialog.filter(e => e.hasError).some(e => e.severity === GqlSeverity.Error)}
        >
          {orderBy(
            validatorDialog.filter(e => e.hasError),
            e => e.severity,
          ).map(e => (
            <ValidatorError key={e.error?.en.short} error={e} />
          ))}
        </ValidatorErrorDialog>
      )}
    </Stack>
  );
};
